import React, { useState, useCallback, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { Box, Typography, AppBar, Toolbar, Select, MenuItem, FormControl, InputLabel, IconButton, Menu, Dialog, DialogContent, ThemeProvider, createTheme, Chip, TextField, useMediaQuery, Divider } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { calendarRowsForExport } from './components/CalendarWidget';
import { weatherRowsForExport } from './components/WeatherWidget';
import { buttonRowsForExport } from './components/ButtonWidget';
import { isQuerylessChartType } from './utils/querylessChartTypes';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import PresentToAllOutlinedIcon from '@mui/icons-material/PresentToAllOutlined';
import QueryEditor from './components/QueryEditor';
import ChartBuilderDialog from './components/ChartBuilderDialog';
import ChartGrid, { tableRowsForDisplay, dateBucketPivotConfigForChart } from './components/ChartGrid';
import { dateBucketPivotColumnOrder } from './utils/pivotDateBucketTable';
import ReportManager from './components/ReportManager';
import {
  PRIMARY_TIMEZONE_IDS,
  formatTimezoneLabel,
  resolveTimezoneSelection,
  loadPreferredTimezone,
  resolveViewTimezone,
  savePreferredTimezone,
} from './utils/timezones';
import DashboardList from './components/DashboardList';
import AdminPanel from './components/AdminPanel';
import AuthRequired, { AuthLoading } from './components/AuthRequired';
import PrintReportPage from './components/PrintReportPage';
import { createRequestGuard } from './utils/requestGuard';
import { useNotification } from './contexts/NotificationContext';
import { StyleTemplateProvider, useStyleTemplate } from './contexts/StyleTemplateContext';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import vsaasTheme from './theme';
import { getDashboardQueryDataKey, buildParamsForChart, getChartDynamicFilters, defaultGridLayoutItem } from './utils/dashboardData';
import { sanitizeDashboardForSave, specForPreviewApi } from './utils/specForApiRequest';
import { downloadDashboardExcel } from './utils/excelExport';
import { logDataConnectionError, NO_DATA_CONNECTIONS_MESSAGE } from './utils/dataConnectionErrors';
import {
  normalizeDashboardLibrary,
  visibleChartsFromDashboard,
  upsertLibraryEntry,
  markHiddenInLibrary,
  showChartOnGrid,
  removeFromLibrary,
  isSharedOriginal,
  isLibraryReference,
  dashboardForDisplay,
  enrichChartConfig,
} from './utils/chartLibrary';
import {
  getDashboardShareCode,
  parseDashboardIdFromUrl,
  syncDashboardIdInUrl,
  isEmbedView,
  stripAuthParamsFromUrl,
  readLastDashboardId,
  rememberLastDashboard,
  forgetLastDashboard,
  forgetLastDashboardIfMatch,
} from './utils/dashboardUrl';
import { saveAuthSession, loadAuthSession, clearAuthSession } from './utils/authSession';
import { drainBufferedHostMessages } from './utils/hostHandshake';
import {
  resolveDashboardAccessLevel,
  isDashboardReadOnly,
} from './utils/dashboardAccess';
import { withPreservedManualPositions } from './utils/imageDensityConfig';
import { useRequestLeave } from './contexts/UnsavedSessionContext';
import { copyTextToClipboard } from './utils/clipboard';
import DashboardAccessChip from './components/DashboardAccessChip';
import './App.css';

/** Toolbar admin (SQL, configuración): solo principal Admin, nunca Manager. */
function resolveIsAdminUser(principalType, adminId, managerId) {
  if (principalType === 'Manager') return false;
  if (principalType === 'Admin') return true;
  return Boolean(adminId) && !managerId;
}

function AppThemeFromContext({ children }) {
  const { siteTheme } = useStyleTemplate();
  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
        ...vsaasTheme.primary,
        main: (siteTheme?.primary && String(siteTheme.primary).trim()) ? String(siteTheme.primary).trim() : vsaasTheme.primary.main,
      },
      secondary: {
        ...vsaasTheme.secondary,
        main: (siteTheme?.secondary && String(siteTheme.secondary).trim()) ? String(siteTheme.secondary).trim() : vsaasTheme.secondary.main,
      },
      background: vsaasTheme.background,
      text: vsaasTheme.text,
    },
    typography: vsaasTheme.typography,
  }), [siteTheme?.primary, siteTheme?.secondary]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

/**
 * Nombre del dashboard en el header, con su propio estado de edición: así cada tecla solo
 * re-renderiza este componente y no todo App.js (que incluiría ChartGrid completo debajo).
 */
const DashboardNameEditor = React.forwardRef(function DashboardNameEditor(
  { name, accessLevel, readOnly, showUnsavedChip, isCompactHeader, onSave, onShowWarning },
  ref
) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');

  const handleStartEditName = () => {
    setEditingName(name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleSaveName = () => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      onShowWarning('El nombre del dashboard no puede estar vacío');
      return;
    }
    onSave(trimmed);
    setIsEditingName(false);
    setEditingName('');
  };

  useImperativeHandle(
    ref,
    () => ({
      // Si se guarda el dashboard mientras se está editando el nombre, confirma ese nombre
      // pendiente en vez de perderlo (mismo comportamiento que antes de aislar este estado).
      commitPendingEdit: () => {
        if (!isEditingName) return;
        const trimmed = editingName.trim();
        if (trimmed) onSave(trimmed);
        setIsEditingName(false);
        setEditingName('');
      },
      // Para cuando se cambia o limpia de dashboard: descarta cualquier edición pendiente
      // sin guardarla, y vuelve al modo de solo lectura.
      resetEditing: () => {
        setIsEditingName(false);
        setEditingName('');
      },
    }),
    [isEditingName, editingName, onSave]
  );

  if (isEditingName) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          autoFocus
          value={editingName}
          onChange={(e) => setEditingName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSaveName();
            } else if (e.key === 'Escape') {
              handleCancelEditName();
            }
          }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.7)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'white',
              },
            },
          }}
          inputProps={{
            style: {
              padding: '4px 8px',
              fontSize: '1.25rem',
              fontWeight: 500,
            },
          }}
        />
        <IconButton
          color="inherit"
          onClick={handleSaveName}
          title="Guardar nombre"
          size="small"
          sx={{
            color: '#4caf50',
            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.2)' },
          }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          color="inherit"
          onClick={handleCancelEditName}
          title="Cancelar"
          size="small"
          sx={{
            color: '#f44336',
            '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.2)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography
        variant={isCompactHeader ? 'subtitle1' : 'h6'}
        component="div"
        sx={{
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          wordBreak: 'break-word',
        }}
      >
        {name || 'Dashboard sin nombre'}
        {accessLevel && (
          <DashboardAccessChip
            accessLevel={accessLevel}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              ...(accessLevel === 'view'
                ? {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    borderColor: 'rgba(255, 255, 255, 0.35)',
                  }
                : {}),
            }}
          />
        )}
        {showUnsavedChip && (
          <Chip
            icon={<EditIcon />}
            label="Sin guardar"
            size="small"
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              backgroundColor: 'rgba(255, 193, 7, 0.9)',
              color: '#000',
              '& .MuiChip-icon': {
                color: '#000',
                fontSize: '0.9rem',
              },
            }}
          />
        )}
      </Typography>
      {!readOnly && (
        <IconButton
          color="inherit"
          onClick={handleStartEditName}
          title="Editar nombre del dashboard"
          size="small"
          sx={{
            opacity: 0.8,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
});

function App() {
  const { showSuccess, showError, showWarning } = useNotification();
  const requestLeave = useRequestLeave();
  const isCompactHeader = useMediaQuery('(max-width:899px)');
  const hideTimezoneInHeader = useMediaQuery('(max-width:1199px)');
  const [queryData, setQueryData] = useState(null);
  const [query, setQuery] = useState('');
  const [charts, setCharts] = useState([]);
  const [weatherDataByChartId, setWeatherDataByChartId] = useState({});
  const handleWeatherDataLoaded = useCallback((chartId, data) => {
    setWeatherDataByChartId((prev) => ({ ...prev, [chartId]: data }));
  }, []);
  const [chartLibrary, setChartLibrary] = useState([]);
  const [chartOriginals, setChartOriginals] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [dataConnections, setDataConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [queryEditorOpen, setQueryEditorOpen] = useState(false);
  const [chartBuilderOpen, setChartBuilderOpen] = useState(false);
  const [builderGridReference, setBuilderGridReference] = useState(null);
  const [editingChartId, setEditingChartId] = useState(null);
  const [queryEditorInitialQueryId, setQueryEditorInitialQueryId] = useState(null);
  const [queryEditorInitialChart, setQueryEditorInitialChart] = useState(null);
  const [queryEditorInitialQueryData, setQueryEditorInitialQueryData] = useState(null);
  const [savedQueries, setSavedQueries] = useState([]);
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const [chartLayout, setChartLayout] = useState([]);
  const [currentDashboardName, setCurrentDashboardName] = useState('');
  const [currentDashboardId, setCurrentDashboardId] = useState(null); // ID del dashboard actual si está cargado
  const currentDashboardIdRef = useRef(null);
  const [currentDashboardAccessLevel, setCurrentDashboardAccessLevel] = useState(null);
  const currentDashboardReadOnly = isDashboardReadOnly({ accessLevel: currentDashboardAccessLevel });
  const [reportManagerOpen, setReportManagerOpen] = useState(false);
  const [dashboardListOpen, setDashboardListOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [dashboardTimezone, setDashboardTimezone] = useState(() => loadPreferredTimezone()); // Preferencia local, no depende de guardar el dashboard
  const dashboardNameEditorRef = useRef(null);
  const [highlightChartId, setHighlightChartId] = useState(null);
  const [collectionRefreshKey, setCollectionRefreshKey] = useState(0);
  // Cache de queryData: clave = queryId o "queryId|param1=val1&param2=val2" para que cada filtro tenga sus propios datos
  const [queryDataByQueryId, setQueryDataByQueryId] = useState({});
  // Clave estable para params (mismo objeto → misma clave). Lógica compartida con previsualización de reportes.
  // Meta (query text + database) por queryId para re-ejecutar con parámetros (filtro dinámico)
  const [queryMetaByQueryId, setQueryMetaByQueryId] = useState({});
  const [chartsDataLoading, setChartsDataLoading] = useState(false);
  // Fuentes de opciones para filtros dinámicos (desde configuración admin / system queries)
  const [dynamicFilterOptionSources, setDynamicFilterOptionSources] = useState([]);
  // Filtros globales a nivel dashboard: paramNames ascendidos y valor seleccionado por param
  const [globalFilterParamNames, setGlobalFilterParamNames] = useState([]);
  const [globalFilterValues, setGlobalFilterValues] = useState({});
  // Alias: nombre del filtro global → nombres de params en SQL (un filtro puede mapear a
  // varios parametros con nombres distintos) para que gráficos sin ese filtro reciban el valor
  const [globalFilterParamAliases, setGlobalFilterParamAliases] = useState({});
  // Modo presentación: oculta acciones de edición/eliminación, para mostrar
  // el dashboard en pantallas/reuniones.
  const [presentationMode, setPresentationMode] = useState(false);

  // Quitar highlight del gráfico tras unos segundos
  useEffect(() => {
    if (!highlightChartId) return;
    const t = setTimeout(() => setHighlightChartId(null), 2500);
    return () => clearTimeout(t);
  }, [highlightChartId]);

  // Estado de autenticación: pending → authenticated | unauthorized
  const [authStatus, setAuthStatus] = useState('pending');
  const [authError, setAuthError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [adminId, setAdminId] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [principalType, setPrincipalType] = useState(null);
  const [userId, setUserId] = useState(null);
  const isAdminUser = useMemo(
    () => resolveIsAdminUser(principalType, adminId, managerId),
    [principalType, adminId, managerId]
  );
  const authBootstrapTimerRef = React.useRef(null);
  const pendingDashboardIdFromUrlRef = useRef(
    typeof window !== 'undefined' ? parseDashboardIdFromUrl() : null
  );
  const urlDashboardLoadedRef = useRef(false);
  const [urlDashboardLoadDone, setUrlDashboardLoadDone] = useState(false);

  // Handshake con el embebedor. Van en refs y no en el closure del efecto porque
  // StrictMode desmonta y vuelve a montar los efectos en desarrollo: si el childId
  // vive en el closure, el HOST_INIT que caiga en el closure descartado se pierde
  // y el padre se queda cargando para siempre.
  const hostRef = useRef({ id: null, source: null, origin: '*' });
  const appReadySentForRef = useRef(null);
  const authStatusRef = useRef('pending');

  const clearAuthBootstrapTimer = useCallback(() => {
    if (authBootstrapTimerRef.current) {
      clearTimeout(authBootstrapTimerRef.current);
      authBootstrapTimerRef.current = null;
    }
  }, []);

  /**
   * Inferir rol por origen del embedder:
   * - admin.vsaas.ai → Admin
   * - suite.vsaas.ai → Manager
   * - otro / desconocido → null (backend prueba ambos endpoints)
   */
  const inferIsAdminFromOrigin = useCallback((originOrUrl) => {
    if (!originOrUrl) return null;
    try {
      const host = new URL(originOrUrl).hostname.toLowerCase();
      if (host === 'admin.vsaas.ai' || host.endsWith('.admin.vsaas.ai')) return true;
      if (host === 'suite.vsaas.ai' || host.endsWith('.suite.vsaas.ai')) return false;
    } catch (_) {
      /* ignore */
    }
    return null;
  }, []);

  /**
   * APP_READY significa "la app ya tiene algo que mostrar", no "la sesión es válida".
   * Si solo se enviara al autenticar, un token rechazado dejaría al padre con su
   * spinner encima de nuestra pantalla de "No autorizado".
   */
  const notifyHostAppReady = useCallback(() => {
    const host = hostRef.current;
    const status = authStatusRef.current;
    if (!host?.id || !host?.source) return;
    if (status !== 'authenticated' && status !== 'unauthorized') return;
    if (appReadySentForRef.current === status) return;
    appReadySentForRef.current = status;
    try {
      host.source.postMessage({ type: 'APP_READY', source: host.id }, host.origin || '*');
    } catch (_) {
      /* el padre pudo cerrarse o cambiar de origen */
    }
  }, []);

  useEffect(() => {
    authStatusRef.current = authStatus;
    notifyHostAppReady();
  }, [authStatus, notifyHostAppReady]);

  const isEmbeddedInIframe = useCallback(() => {
    try {
      return window.self !== window.top;
    } catch (_) {
      return true; // cross-origin iframe: acceso a top bloqueado
    }
  }, []);

  const validateTokenMutation = useMutation({
    mutationFn: (body) => axios.post('/api/auth/validate', body),
  });
  // El arranque por sesion guardada y el mensaje del host pueden validar el mismo token a la vez.
  // El guard comparte esa peticion en vuelo en vez de enviarla dos veces.
  const validateGuardRef = useRef(null);
  if (!validateGuardRef.current) validateGuardRef.current = createRequestGuard();

  // isAdmin: true | false | null (null = auto / probar ambos)
  const validateToken = useCallback(async (token, id, isAdmin = null) => {
    try {
      const body = { token, id, isAdmin }; // null → backend intenta Admin y luego Manager
      const response = await validateGuardRef.current(`validate:${JSON.stringify(body)}`, () =>
        validateTokenMutation.mutateAsync(body)
      );

      if (response.data.success) {
        clearAuthBootstrapTimer();
        setAuthToken(token);
        setUserId(response.data.userId);
        if (response.data.adminId) {
          setAdminId(response.data.adminId);
          setManagerId(null);
          setPrincipalType('Admin');
        } else {
          setManagerId(response.data.managerId);
          setAdminId(null);
          setPrincipalType(
            response.data.principalType === 'Admin' ? 'Admin' : 'Manager'
          );
        }
        setIsAuthenticated(true);
        setAuthStatus('authenticated');
        setAuthError(null);
        saveAuthSession({
          token,
          adminId: response.data.adminId || null,
          managerId: response.data.managerId || null,
          userId: response.data.userId || null,
        });
        stripAuthParamsFromUrl();
        return true;
      }
      clearAuthBootstrapTimer();
      clearAuthSession();
      setIsAuthenticated(false);
      setAuthToken(null);
      setManagerId(null);
      setAdminId(null);
      setPrincipalType(null);
      setUserId(null);
      setAuthStatus('unauthorized');
      setAuthError(response.data.error || 'Error de autorización.');
      return false;
    } catch (error) {
      console.error('Error al validar token:', error);
      clearAuthBootstrapTimer();
      clearAuthSession();
      setIsAuthenticated(false);
      setAuthToken(null);
      setManagerId(null);
      setAdminId(null);
      setPrincipalType(null);
      setUserId(null);
      setAuthStatus('unauthorized');
      setAuthError(error.response?.data?.error || 'Error de autorización.');
      return false;
    }
  }, [clearAuthBootstrapTimer]);

  // Bootstrap: token en URL → sessionStorage → postMessage (iframe) → no autorizado
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const managerIdFromUrl = urlParams.get('managerId');
    const adminIdFromUrl = urlParams.get('adminId');
    const userIdFromUrl = urlParams.get('userId');
    const isAdminParam = urlParams.get('isAdmin');
    const referrerHint = inferIsAdminFromOrigin(document.referrer);

    if (tokenFromUrl) {
      if (adminIdFromUrl) {
        validateToken(tokenFromUrl, adminIdFromUrl, true);
      } else if (managerIdFromUrl) {
        validateToken(tokenFromUrl, managerIdFromUrl, false);
      } else if (userIdFromUrl) {
        let hint = null;
        if (isAdminParam === 'true') hint = true;
        else if (isAdminParam === 'false') hint = false;
        else hint = referrerHint;
        validateToken(tokenFromUrl, userIdFromUrl, hint);
      } else {
        setAuthStatus('unauthorized');
        setAuthError('Error de autorización.');
      }
      return undefined;
    }

    const session = loadAuthSession();
    if (session?.token && (session.adminId || session.managerId)) {
      validateToken(session.token, session.adminId || session.managerId, !!session.adminId);
      return undefined;
    }

    if (isEmbeddedInIframe()) {
      authBootstrapTimerRef.current = setTimeout(() => {
        setAuthStatus((current) => {
          if (current === 'pending') {
            setAuthError('Error de autorización.');
            return 'unauthorized';
          }
          return current;
        });
      }, 12000);
      return () => clearAuthBootstrapTimer();
    }

    setAuthStatus('unauthorized');
    setAuthError('Error de autorización.');
    return undefined;
  }, [validateToken, inferIsAdminFromOrigin, isEmbeddedInIframe, clearAuthBootstrapTimer]);

  // Un unico listener para los dos protocolos del embebedor:
  //  - mensaje simple: { token, adminId|managerId|id }
  //  - handshake HOST_INIT -> CHILD_READY -> HOST_BOOTSTRAP -> APP_READY, usado por
  //    embedded-container.component.js de xompass-suite (wait-for-ready-message="true").
  useEffect(() => {
    const mapUserTypeToIsAdmin = (userType) => {
      if (userType === 'Manager') return false;
      if (userType === 'Admin' || userType === 'SuperAdmin') return true;
      // SupportAdmin u otro tipo: sin endpoint de validacion propio en este backend,
      // se deja null para que /api/auth/validate intente ambos.
      return null;
    };

    const handleHostInit = (event) => {
      const id = event.data?.payload?.id || null;
      if (!id || !event.source) return;
      const previousId = hostRef.current?.id;
      hostRef.current = { id, source: event.source, origin: event.origin || '*' };
      // Contenedor recreado: el padre solo acepta un APP_READY con el childId nuevo.
      if (previousId !== id) appReadySentForRef.current = null;
      try {
        event.source.postMessage({ type: 'CHILD_READY', source: id }, event.origin || '*');
      } catch (_) {
        return;
      }
      // Si el HOST_INIT llega tarde, la sesión ya puede estar resuelta y authStatus
      // no volverá a cambiar: hay que avisar desde aquí.
      notifyHostAppReady();
    };

    const handleMessage = async (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'HOST_INIT') {
        handleHostInit(event);
        return;
      }

      if (data.type === 'HOST_BOOTSTRAP') {
        const payload = data.payload || {};
        if (!payload.token || !payload.userId) return;
        console.log('🔐 postMessage: HOST_BOOTSTRAP', payload.userType, event.origin);
        await validateToken(payload.token, payload.userId, mapUserTypeToIsAdmin(payload.userType));
        return;
      }

      if (!data.token) return;

      const token = data.token;
      const adminIdFromMessage = data.adminId;
      const managerIdFromMessage = data.managerId;
      const idFromMessage = data.id || data.userId;

      if (adminIdFromMessage) {
        console.log('🔐 postMessage: Admin', event.origin);
        await validateToken(token, adminIdFromMessage, true);
      } else if (managerIdFromMessage) {
        console.log('🔐 postMessage: Manager', event.origin);
        await validateToken(token, managerIdFromMessage, false);
      } else if (idFromMessage) {
        let hint = null;
        if (data.isAdmin === true) hint = true;
        else if (data.isAdmin === false) hint = false;
        else hint = inferIsAdminFromOrigin(event.origin);
        console.log(
          `🔐 postMessage: id, rol=${hint === true ? 'Admin' : hint === false ? 'Manager' : 'auto'}`,
          event.origin
        );
        await validateToken(token, idFromMessage, hint);
      }
    };

    window.addEventListener('message', handleMessage);
    // Mensajes llegados antes de montar este listener (ver utils/hostHandshake).
    drainBufferedHostMessages(handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [validateToken, inferIsAdminFromOrigin, notifyHostAppReady]);

  // Validar sesión periódicamente (cada 5 minutos)
  useEffect(() => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) return;
    
    const interval = setInterval(async () => {
      console.log('🔄 Validando sesión...');
      const id = adminId || managerId;
      const isAdmin = !!adminId;
      const isValid = await validateToken(authToken, id, isAdmin);
      if (!isValid) {
        console.warn('⚠️ Sesión expirada o inválida');
        setAuthError('Tu sesión ha expirado.');
      }
    }, 5 * 60 * 1000); // 5 minutos
    
    return () => clearInterval(interval);
  }, [isAuthenticated, authToken, managerId, adminId, validateToken]);

  // Helper para obtener headers de autenticación
  const getAuthHeaders = useCallback(() => {
    const headers = { 'Authorization': authToken };
    if (adminId) headers['X-Admin-Id'] = adminId;
    else if (managerId) headers['X-Manager-Id'] = managerId;
    return headers;
  }, [authToken, adminId, managerId]);

  const chartOriginalsMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/chart-originals', { headers }),
  });

  const loadChartOriginals = useCallback(async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      setChartOriginals([]);
      return;
    }
    try {
      const res = await chartOriginalsMutation.mutateAsync(getAuthHeaders());
      if (res.data.success) setChartOriginals(res.data.originals || []);
    } catch {
      setChartOriginals([]);
    }
  }, [isAuthenticated, authToken, managerId, adminId, getAuthHeaders]);

  useEffect(() => {
    if (chartBuilderOpen) return;
    loadChartOriginals();
  }, [loadChartOriginals, chartBuilderOpen]);

  const sharedOriginalIds = useMemo(() => {
    const set = new Set();
    for (const o of chartOriginals) {
      if (isSharedOriginal(o)) set.add(o.id);
    }
    return set;
  }, [chartOriginals]);

  // Re-ejecutar la query de un gráfico con parámetros
  const chartsRef = useRef(charts);
  chartsRef.current = charts;

  useEffect(() => {
    currentDashboardIdRef.current = currentDashboardId;
  }, [currentDashboardId]);

  const queryFetchParams = useCallback(() => {
    const dashId = currentDashboardIdRef.current;
    return dashId != null && dashId !== ''
      ? { dashboardId: dashId }
      : undefined;
  }, []);

  const resolveDashboardQueryDataKey = useCallback(
    (queryId, params) => getDashboardQueryDataKey(queryId, params, dashboardTimezone),
    [dashboardTimezone]
  );


  const handleExportDashboardExcel = useCallback(() => {
    const chartsWithData = (charts || []).map((chart) => {
      if (chart.type === 'weather') {
        return { chart, rows: weatherRowsForExport(weatherDataByChartId[chart.id]) };
      }
      if (chart.type === 'calendar') {
        return { chart, rows: calendarRowsForExport(chart.calendarEvents) };
      }
      if (chart.type === 'button') {
        return { chart, rows: buttonRowsForExport(chart) };
      }
      const params = buildParamsForChart(chart, {
        globalFilterValues,
        globalFilterParamAliases,
        globalFilterParamNames,
        getDynamicFilterValue: (c, paramName) => {
          const df = getChartDynamicFilters(c).find(
            (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
          );
          return df?.selectedValue;
        },
      });
      const dataKey = resolveDashboardQueryDataKey(chart.queryId, params);
      const dataForKey = chart.queryId != null ? queryDataByQueryId[dataKey] : null;
      const rawRows = dataForKey?.data ?? (chart.queryId == null ? queryData?.data : null) ?? [];
      // Si el tile es una tabla pivoteada, exportar el dashboard completo debe
      // respetar ese pivote igual que el export del tile individual: mismo encabezado
      // amigable para la columna de fila y mismo orden de columnas de la tabla en pantalla.
      const pivotConfig = dateBucketPivotConfigForChart(chart, rawRows);
      return {
        chart,
        rows: tableRowsForDisplay(chart, rawRows),
        extraHeaderMap: pivotConfig?.rowHeaderLabel ? { [pivotConfig.rowKey]: pivotConfig.rowHeaderLabel } : undefined,
        columnOrder: pivotConfig ? dateBucketPivotColumnOrder(pivotConfig.rowKey, pivotConfig.bucketType) : undefined,
        // La tabla pivoteada siempre muestra la columna Total en pantalla
        // (showTotalColumn sin condicion en ese branch de ChartGrid.js).
        includeRowTotal: Boolean(pivotConfig),
      };
    });
    downloadDashboardExcel(currentDashboardName, chartsWithData);
  }, [
    charts,
    globalFilterValues,
    globalFilterParamAliases,
    globalFilterParamNames,
    resolveDashboardQueryDataKey,
    queryDataByQueryId,
    queryData,
    currentDashboardName,
    weatherDataByChartId,
  ]);

  // Evita exportar mientras algún gráfico del dashboard todavía no trae su primera respuesta.
  const dashboardExportPending = useMemo(() => {
    return (charts || []).some((chart) => {
      if (chart.type === 'weather') return !weatherDataByChartId[chart.id];
      if (chart.queryId == null) return false;
      const params = buildParamsForChart(chart, {
        globalFilterValues,
        globalFilterParamAliases,
        globalFilterParamNames,
        getDynamicFilterValue: (c, paramName) => {
          const df = getChartDynamicFilters(c).find(
            (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
          );
          return df?.selectedValue;
        },
      });
      const dataKey = resolveDashboardQueryDataKey(chart.queryId, params);
      return queryDataByQueryId[dataKey] == null;
    });
  }, [charts, globalFilterValues, globalFilterParamAliases, globalFilterParamNames, resolveDashboardQueryDataKey, queryDataByQueryId, weatherDataByChartId]);

  const queryMetaMutation = useMutation({
    mutationFn: ({ queryId, headers, params }) =>
      axios.get(`/api/queries/${queryId}`, { headers, params }),
  });
  const chartBuilderPreviewMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/preview', body, { headers }),
  });
  const runQueryMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/query', body, { headers }),
  });

  const runQueryWithParamsOnce = useCallback(async (queryId, params, chartHint = null) => {
    const cacheKey = resolveDashboardQueryDataKey(queryId, params || {});
    const requestSeq = (dashboardQueryRequestSeqByKey.current.get(cacheKey) || 0) + 1;
    dashboardQueryRequestSeqByKey.current.set(cacheKey, requestSeq);
    const isLatest = () => dashboardQueryRequestSeqByKey.current.get(cacheKey) === requestSeq;
    // Sin esto, una query que falla (red caída, error de servidor, meta no encontrada) deja
    // el cache sin esa clave para siempre: ChartGrid.js nunca distingue "todavía no llegó" de
    // "falló", y el gráfico queda en spinner infinito en vez de mostrarse vacío.
    const markFailed = () => {
      if (!isLatest()) return;
      const failedPayload = { data: [], error: true };
      queryDataByQueryIdRef.current = { ...queryDataByQueryIdRef.current, [cacheKey]: failedPayload };
      setQueryDataByQueryId((prev) => ({ ...prev, [cacheKey]: failedPayload }));
    };
    const chart =
      chartHint || (chartsRef.current || []).find((c) => String(c?.queryId) === String(queryId)) || null;
    const mergedParams = { ...(params || {}), __dashboard_tz: dashboardTimezone };

    let meta = queryMetaByQueryIdRef.current[queryId];
    if (!meta?.queryText && !chart?.visualizationSpec) {
      try {
        const res = await queryMetaMutation.mutateAsync({
          queryId,
          headers: getAuthHeaders(),
          params: queryFetchParams(),
        });
        if (!isLatest()) return null;
        if (res.data?.success && res.data.query) {
          meta = {
            queryText: res.data.query.query,
            database: res.data.query.database,
            connectionId: res.data.query.connectionId || selectedConnectionId,
          };
          queryMetaByQueryIdRef.current = {
            ...queryMetaByQueryIdRef.current,
            [queryId]: meta,
          };
          setQueryMetaByQueryId((prev) => ({ ...prev, [queryId]: meta }));
        }
      } catch (e) {
        if (!isLatest()) return null;
        console.warn('Error al obtener meta de query para filtro dinámico:', queryId, e);
        markFailed();
        return null;
      }
    }

    const db = meta?.database || selectedDatabase;
    const connId = meta?.connectionId || selectedConnectionId || undefined;

    try {
      let payload = null;

      if (chart?.visualizationSpec) {
        const res = await chartBuilderPreviewMutation.mutateAsync({
          body: {
            connectionId: connId,
            database: db,
            spec: specForPreviewApi(chart.visualizationSpec),
            timezone: dashboardTimezone,
            parameters: mergedParams,
          },
          headers: getAuthHeaders(),
        });
        if (!isLatest()) return null;
        if (res.data?.success) {
          payload = {
            columns: res.data.columns,
            data: res.data.data,
            ...(res.data.kpiComparison != null ? { kpiComparison: res.data.kpiComparison } : {}),
            // Necesario para graficos bar/line con comparacion de periodo activa: esa
            // consulta devuelve un dataset con nombres de columna distintos a los que el
            // grafico tiene guardados (ver ChartGrid.js), y el mapping es la unica fuente
            // confiable de cuales son.
            ...(res.data.mapping != null ? { mapping: res.data.mapping } : {}),
          };
        }
      } else {
        if (!meta?.queryText) {
          markFailed();
          return null;
        }
        const res = await runQueryMutation.mutateAsync({
          body: {
            query: meta.queryText,
            database: db,
            connectionId: connId,
            parameters: mergedParams,
            timezone: dashboardTimezone,
          },
          headers: getAuthHeaders(),
        });
        if (!isLatest()) return null;
        if (res.data?.success) {
          payload = { columns: res.data.columns, data: res.data.data };
        }
      }

      if (payload) {
        if (!isLatest()) return null;
        queryDataByQueryIdRef.current = { ...queryDataByQueryIdRef.current, [cacheKey]: payload };
        setQueryDataByQueryId((prev) => ({ ...prev, [cacheKey]: payload }));
        return payload;
      }
    } catch (e) {
      if (!isLatest()) return null;
      console.error('Error al ejecutar query con parámetros:', e);
      markFailed();
      return null;
    }
    // Sin excepción pero sin payload (ej. success:false del servidor): mismo caso, falló.
    markFailed();
    return null;
  }, [
    selectedDatabase,
    selectedConnectionId,
    getAuthHeaders,
    resolveDashboardQueryDataKey,
    dashboardTimezone,
    queryFetchParams,
  ]);

  const requestGuardRef = useRef(null);
  if (!requestGuardRef.current) requestGuardRef.current = createRequestGuard();
  const runQueryWithParams = useCallback(
    (queryId, params, chartHint = null, options = {}) => {
      const cacheKey = resolveDashboardQueryDataKey(queryId, params || {});
      return requestGuardRef.current(
        cacheKey,
        () => runQueryWithParamsOnce(queryId, params, chartHint),
        { cached: queryDataByQueryIdRef.current[cacheKey], soloSiFalta: options.soloSiFalta }
      );
    },
    [runQueryWithParamsOnce, resolveDashboardQueryDataKey]
  );

  const timezoneRefreshReady = useRef(false);
  const lastReloadKeyRef = useRef(null);
  useEffect(() => {
    const reloadKey = `${dashboardTimezone}|${currentDashboardId ?? ''}`;
    if (!timezoneRefreshReady.current) {
      timezoneRefreshReady.current = true;
      lastReloadKeyRef.current = reloadKey;
      return undefined;
    }
    if (lastReloadKeyRef.current === reloadKey) return undefined;
    lastReloadKeyRef.current = reloadKey;
    if (!isAuthenticated || !charts?.length) return undefined;
    setQueryDataByQueryId({});
    queryDataByQueryIdRef.current = {};
    let cancelled = false;
    (async () => {
      for (const chart of charts) {
        if (cancelled || !chart?.queryId) continue;
        const params = buildParamsForChart(chart, {
          globalFilterValues,
          globalFilterParamAliases,
          globalFilterParamNames,
          getDynamicFilterValue: (c, paramName) => {
            const df = getChartDynamicFilters(c).find(
              (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
            );
            return df?.selectedValue;
          },
        });
        await runQueryWithParams(chart.queryId, params, chart);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dashboardTimezone,
    currentDashboardId,
    isAuthenticated,
    charts,
    globalFilterValues,
    globalFilterParamAliases,
    globalFilterParamNames,
    runQueryWithParams,
  ]);

  // Ejecutar una query de opciones (desplegable filtro dinámico)
  const runOptionsQuery = useCallback(async (optionsQueryText, database, connectionId) => {
    const db = database || selectedDatabase;
    const connId = connectionId || selectedConnectionId;
    const q = (optionsQueryText || '').trim();
    if (!q) return null;
    try {
      const res = await runQueryMutation.mutateAsync({
        body: {
          query: q,
          database: db,
          connectionId: connId || undefined,
          timezone: dashboardTimezone,
        },
        headers: getAuthHeaders(),
      });
      return res.data?.success ? { columns: res.data.columns, data: res.data.data } : null;
    } catch (e) {
      console.warn('Error al ejecutar query de opciones:', e?.response?.data?.error || e.message || e);
      return null;
    }
  }, [selectedDatabase, selectedConnectionId, getAuthHeaders, dashboardTimezone]);

  const filterOptionsMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/filter-options', body, { headers }),
  });

  // Opciones de filtro dinámico con cascada (chart builder + visualizationSpec)
  const runFilterOptionsQuery = useCallback(async (chart, paramName, parameters, database, connectionId) => {
    if (!chart?.visualizationSpec || !paramName) return null;
    const db = database || selectedDatabase;
    const connId = connectionId || selectedConnectionId;
    const body = {
      connectionId: connId || undefined,
      database: db,
      spec: specForPreviewApi(chart.visualizationSpec),
      paramName: String(paramName).trim(),
      parameters: parameters || {},
      timezone: dashboardTimezone,
    };
    try {
      // Dos gráficos con el mismo filtro dinámico piden la misma consulta. La clave deja fuera
      // visualization, porque las opciones solo dependen de filters y de los parámetros. El guard
      // comparte la petición en vuelo en vez de lanzar un DISTINCT idéntico por cada uno.
      const { visualization: _visualization, ...specParaClave } = body.spec || {};
      const res = await requestGuardRef.current(
        `filter-options:${JSON.stringify({ ...body, spec: specParaClave })}`,
        () => filterOptionsMutation.mutateAsync({ body, headers: getAuthHeaders() })
      );
      return res.data?.success ? { columns: res.data.columns, data: res.data.data } : null;
    } catch (e) {
      console.warn('Error al cargar opciones en cascada:', e?.response?.data?.error || e.message || e);
      return null;
    }
  }, [selectedDatabase, selectedConnectionId, getAuthHeaders, dashboardTimezone]);

  const stylePaletteGetMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/style-palette', { headers }),
  });
  // El proveedor de paleta puede pedirla dos veces al mismo tiempo; el guard comparte esa peticion.
  const stylePaletteGuardRef = useRef(null);
  if (!stylePaletteGuardRef.current) stylePaletteGuardRef.current = createRequestGuard();

  const loadStylePalette = useCallback(async () => {
    if (!authToken || (!managerId && !adminId)) return null;
    try {
      const headers = getAuthHeaders();
      const { data } = await stylePaletteGuardRef.current(
        `style-palette:${authToken}|${managerId || ''}|${adminId || ''}`,
        () => stylePaletteGetMutation.mutateAsync(headers)
      );
      return data.success ? data : null;
    } catch (e) {
      console.warn('Error al cargar paleta:', e);
      return null;
    }
  }, [authToken, managerId, adminId, getAuthHeaders]);

  const stylePalettePutMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/style-palette', body, { headers }),
  });

  const saveStylePalette = useCallback(async (payload) => {
    if (!authToken || (!managerId && !adminId)) return;
    await stylePalettePutMutation.mutateAsync({ body: payload, headers: getAuthHeaders() });
  }, [authToken, managerId, adminId, getAuthHeaders]);

  // Cargar datos cuando cambie el estado de autenticación
  useEffect(() => {
    if (isAuthenticated && authToken && (managerId || adminId)) {
      loadSavedDashboards();
      loadSavedQueries();
    }
  }, [isAuthenticated, authToken, managerId, adminId]);

  // Sembrar cache con queryData actual cuando hay query ejecutada (para que gráficos con ese queryId usen datos actuales)
  useEffect(() => {
    if (selectedQueryId != null && queryData != null) {
      setQueryDataByQueryId(prev => ({
        ...prev,
        [selectedQueryId]: queryData,
      }));
    }
  }, [selectedQueryId, queryData]);

  // Ref para leer la cache actual sin poner queryDataByQueryId en deps del efecto de fetch
  const queryDataByQueryIdRef = React.useRef(queryDataByQueryId);
  React.useEffect(() => {
    queryDataByQueryIdRef.current = queryDataByQueryId;
  }, [queryDataByQueryId]);
  const queryMetaByQueryIdRef = React.useRef(queryMetaByQueryId);
  const dashboardQueryRequestSeqByKey = useRef(new Map());
  React.useEffect(() => {
    queryMetaByQueryIdRef.current = queryMetaByQueryId;
  }, [queryMetaByQueryId]);

  // Cargar queryData por queryId para cada gráfico del dashboard (cada gráfico usa datos de su query, no solo queryData global)
  useEffect(() => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId) || !charts.length) {
      setChartsDataLoading(false);
      return;
    }
    const queryIds = [...new Set(charts.map(c => c.queryId).filter(Boolean))];
    if (queryIds.length === 0) {
      setChartsDataLoading(false);
      return;
    }

    let cancelled = false;
    setChartsDataLoading(true);
    const fetchMissing = async () => {
      try {
        for (const qid of queryIds) {
          if (cancelled) return;
          const chartForQuery = charts.find((c) => String(c?.queryId) === String(qid));
          const params = buildParamsForChart(chartForQuery || {}, {
            globalFilterValues,
            globalFilterParamAliases,
            globalFilterParamNames,
            getDynamicFilterValue: (c, paramName) => {
              const df = getChartDynamicFilters(c).find(
                (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
              );
              return df?.selectedValue;
            },
          });
          const cacheKey = resolveDashboardQueryDataKey(qid, params);
          if (queryDataByQueryIdRef.current[cacheKey] != null) continue;
          const knownMeta = queryMetaByQueryIdRef.current[qid];
          if (knownMeta?.queryText || chartForQuery?.visualizationSpec) {
            await runQueryWithParams(qid, params, chartForQuery);
            continue;
          }
          try {
            const res = await queryMetaMutation.mutateAsync({
              queryId: qid,
              headers: getAuthHeaders(),
              params: queryFetchParams(),
            });
            if (cancelled) return;
            if (queryDataByQueryIdRef.current[cacheKey] != null) continue;
            if (res.data?.success && res.data.query) {
              const q = res.data.query;
              const meta = {
                queryText: q.query,
                database: q.database,
                connectionId: q.connectionId || selectedConnectionId,
              };
              queryMetaByQueryIdRef.current = {
                ...queryMetaByQueryIdRef.current,
                [qid]: meta,
              };
              setQueryMetaByQueryId((prev) => ({ ...prev, [qid]: meta }));

              const hasCachedData =
                !chartForQuery?.visualizationSpec &&
                Object.keys(params).length === 0 &&
                q.queryData &&
                (Array.isArray(q.queryData.data) ? q.queryData.data.length > 0 : q.queryData.data != null);
              if (hasCachedData) {
                setQueryDataByQueryId((prev) => {
                  const next = { ...prev, [cacheKey]: q.queryData };
                  queryDataByQueryIdRef.current = next;
                  return next;
                });
              } else if (meta.queryText || chartForQuery?.visualizationSpec) {
                await runQueryWithParams(qid, params, chartForQuery);
              }
            }
          } catch (e) {
            if (chartForQuery?.visualizationSpec) {
              await runQueryWithParams(qid, params, chartForQuery);
            } else {
              console.warn('Error al cargar datos de query para gráfico:', qid, e);
            }
          }
        }
      } finally {
        if (!cancelled) setChartsDataLoading(false);
      }
    };
    fetchMissing();
    return () => { cancelled = true; setChartsDataLoading(false); };
  }, [
    isAuthenticated,
    authToken,
    managerId,
    adminId,
    charts,
    currentDashboardId,
    getAuthHeaders,
    runQueryWithParams,
    selectedConnectionId,
    queryFetchParams,
    resolveDashboardQueryDataKey,
    globalFilterValues,
    globalFilterParamAliases,
    globalFilterParamNames,
  ]);

  const dynamicFilterSourcesMutation = useMutation({
    mutationFn: ({ headers, params }) => axios.get('/api/dynamic-filter-sources', { headers, params }),
  });

  const loadDynamicFilterSources = async (connectionId, database) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) return;
    try {
      const params = {};
      if (connectionId) params.connectionId = connectionId;
      if (database) params.database = database;
      const res = await dynamicFilterSourcesMutation.mutateAsync({ headers: getAuthHeaders(), params });
      if (res.data.success) {
        if (Array.isArray(res.data.sources)) setDynamicFilterOptionSources(res.data.sources);
        if (res.data.globalFilterParamAliases && typeof res.data.globalFilterParamAliases === 'object') {
          setGlobalFilterParamAliases(res.data.globalFilterParamAliases);
        }
      }
    } catch (err) {
      console.warn('No se pudieron cargar fuentes de filtros dinámicos:', err);
    }
  };

  // Cargar conexiones y bases de datos después de autenticación
  useEffect(() => {
    if (isAuthenticated && authToken && (managerId || adminId)) {
      (async () => {
        const { defId, connections } = await loadDataConnections();
        if (!connections.length) {
          console.warn('⚠️ Sin conexiones de BD para datos. Configúralas en Admin → Conexión BD.');
          return;
        }
        await loadDatabases(defId || selectedConnectionId, connections);
        loadDynamicFilterSources(defId || selectedConnectionId, selectedDatabase);
      })();
    } else {
      // Limpiar bases de datos si no está autenticado
      setAvailableDatabases([]);
      setSelectedDatabase('');
      setDataConnections([]);
      setSelectedConnectionId('');
      setDynamicFilterOptionSources([]);
      setGlobalFilterParamAliases({});
    }
  }, [isAuthenticated, authToken, managerId, adminId]);

  const savedQueriesGetMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/queries', { headers }),
  });

  const loadSavedQueries = async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      console.warn('No autenticado, no se pueden cargar queries');
      return;
    }

    try {
      const response = await savedQueriesGetMutation.mutateAsync(getAuthHeaders());
      
      if (response.data.success) {
        setSavedQueries(response.data.queries || []);
      }
    } catch (error) {
      console.error('Error al cargar queries desde BD:', error);
    }
  };

  const saveQueryMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/queries/save', body, { headers }),
  });

  const saveQuery = async (queryText, queryData, database, queryId = null, name = null, chatHistory = null, suggestedFilterConfig = null, dynamicFilterConfig = null, connectionId = null) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      console.warn('No autenticado, no se puede guardar query');
      return null;
    }
    
    const queryEntry = {
      id: queryId || Date.now(),
      query: queryText,
      queryData: queryData,
      database: database || selectedDatabase,
      connectionId: connectionId || selectedConnectionId || 'default',
      createdAt: new Date().toISOString(),
      name: name || `Query ${new Date().toLocaleTimeString()}`,
      chatHistory: chatHistory || [] // El chat es la fuente de verdad
    };
    if (suggestedFilterConfig && (Array.isArray(suggestedFilterConfig.filters) || (suggestedFilterConfig.defaultValues && Object.keys(suggestedFilterConfig.defaultValues).length > 0))) {
      queryEntry.suggestedFilters = suggestedFilterConfig.filters || [];
      queryEntry.suggestedFilterDefaults = suggestedFilterConfig.defaultValues || {};
    }
    if (dynamicFilterConfig && typeof dynamicFilterConfig === 'object') {
      // Guardar solo si hay algo relevante (paramName u optionsQuery, etc.)
      const hasContent = Object.keys(dynamicFilterConfig).some((k) => {
        const v = dynamicFilterConfig[k];
        if (k === 'enabled') return !!v;
        if (v == null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return Object.keys(v).length > 0;
        return true;
      });
      if (hasContent) {
        queryEntry.dynamicFilterConfig = dynamicFilterConfig;
      }
    }
    
    try {
      const response = await saveQueryMutation.mutateAsync({ body: queryEntry, headers: getAuthHeaders() });
      
      if (response.data.success) {
        queryEntry.id = response.data.id;
        // Actualizar lista ligera local (solo metadata para el listado)
        const lightItem = {
          id: queryEntry.id,
          name: queryEntry.name,
          query: (queryEntry.query || '').substring(0, 300),
          database: queryEntry.database,
          connectionId: queryEntry.connectionId,
          recordCount: Array.isArray(queryEntry.queryData?.data) ? queryEntry.queryData.data.length : null,
          updatedAt: new Date().toISOString(),
          createdAt: queryEntry.createdAt || new Date().toISOString()
        };
        setSavedQueries(prev => [lightItem, ...prev.filter(q => q.id !== queryEntry.id)]);
        setSelectedQueryId(queryEntry.id);
        return queryEntry.id;
      }
    } catch (error) {
      console.error('Error al guardar query en BD:', error);
      throw error;
    }
    
    return null;
  };

  const dataConnectionsGetMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/data-connections', { headers }),
  });

  const loadDataConnections = async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) return { defId: '', connections: [] };
    try {
      const res = await dataConnectionsGetMutation.mutateAsync(getAuthHeaders());
      if (res.data.success) {
        const list = res.data.dataConnections || [];
        setDataConnections(list);
        if (!list.length) {
          console.warn(`⚠️ ${NO_DATA_CONNECTIONS_MESSAGE}`);
        }
        const defId = res.data.defaultConnectionId || list.find((c) => c.isDefault)?.id || list[0]?.id || '';
        setSelectedConnectionId((prev) => prev || defId);
        return { defId, connections: list };
      }
    } catch (err) {
      console.warn('No se pudieron cargar conexiones de datos:', err);
    }
    return { defId: selectedConnectionId, connections: dataConnections };
  };

  const databasesGetMutation = useMutation({
    mutationFn: ({ headers, params }) => axios.get('/api/databases', { headers, params }),
  });

  const loadDatabases = async (connectionId, connectionsOverride) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      console.warn('⚠️ No autenticado, no se pueden cargar bases de datos');
      return;
    }

    const connId = connectionId || selectedConnectionId;
    const connections = connectionsOverride || dataConnections;
    
    try {
      console.log('🔄 Cargando bases de datos disponibles...', connId || '(default)');
      const params = connId ? { connectionId: connId } : {};
      const response = await databasesGetMutation.mutateAsync({ headers: getAuthHeaders(), params });
      console.log('📊 Respuesta de /api/databases:', response.data);
      if (response.data.success) {
        const databases = response.data.databases || [];
        console.log('✅ Bases de datos cargadas:', databases);
        setAvailableDatabases(databases);
        const conn = connections.find((c) => c.id === connId);
        const configured = conn?.database?.trim();
        setSelectedDatabase((prev) => {
          if (prev) {
            const kept = databases.find((db) => db === prev || db.toLowerCase() === prev.toLowerCase());
            if (kept) return kept;
          }
          if (configured) {
            const match = databases.find((db) => db === configured || db.toLowerCase() === configured.toLowerCase());
            if (match) return match;
            return configured;
          }
          if (databases.length > 0) return databases[0];
          return prev;
        });
      } else {
        console.warn('⚠️ Respuesta no exitosa:', response.data);
      }
    } catch (error) {
      if (!logDataConnectionError('Carga de bases de datos', error)) {
        console.error('❌ Error al cargar bases de datos:', error);
        console.error('   Detalles:', error.response?.data || error.message);
      }
      if (error.response?.status === 401) {
        console.error('⚠️ No autorizado para ver bases de datos');
        setIsAuthenticated(false);
      }
    }
  };

  const dashboardsGetMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/dashboards', { headers }),
  });

  const loadSavedDashboards = async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      console.warn('No autenticado, no se pueden cargar dashboards');
      return;
    }

    try {
      const response = await dashboardsGetMutation.mutateAsync(getAuthHeaders());
      
      if (response.data.success) {
        const dashboards = response.data.dashboards || [];
        setSavedDashboards(dashboards);
      }
    } catch (error) {
      console.error('Error al cargar dashboards desde BD:', error);
    }
  };

  const handleQueryExecute = useCallback((data, queryText, database) => {
    setQueryData(data);
    setQuery(queryText);
    if (selectedQueryId != null && queryText != null) {
      setQueryMetaByQueryId(prev => ({ ...prev, [selectedQueryId]: { queryText, database: database || selectedDatabase } }));
    }
    // Si se ejecuta una nueva query y no hay dashboard cargado, limpiar el ID para crear uno nuevo
    if (!currentDashboardId) {
      setCurrentDashboardId(null);
    }
    // No crear automáticamente nuevas queries, solo actualizar los datos
    // La query se guardará automáticamente desde QueryEditor después de ejecutarse
  }, [currentDashboardId, selectedQueryId, selectedDatabase]);

  const handleAddChartFromEditor = useCallback(
    (chartConfig, chartQueryData, queryMeta) => {
      const enrichedConfig = enrichChartConfig(chartConfig, chartQueryData?.data);
      const newChart = {
        id: `chart-${Date.now()}`,
        ...enrichedConfig,
      };
      const nextCharts = [...charts, newChart];
      const maxY = chartLayout.length > 0 ? Math.max(...chartLayout.map((item) => item.y + item.h)) : 0;
      const nextLayout = [
        ...chartLayout,
        defaultGridLayoutItem(newChart.id, newChart.type, { y: maxY }),
      ];
      const nextLibrary = upsertLibraryEntry(chartLibrary, newChart, { visibleOnGrid: true });
      setCharts(nextCharts);
      setChartLayout(nextLayout);
      setChartLibrary(nextLibrary);
      if (chartConfig.queryId != null && chartQueryData != null) {
        const params = buildParamsForChart(newChart, {
          globalFilterValues,
          globalFilterParamAliases,
          globalFilterParamNames,
          getDynamicFilterValue: (c, paramName) =>
            getChartDynamicFilters(c).find(
              (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
            )?.selectedValue,
        });
        const cacheKey = resolveDashboardQueryDataKey(chartConfig.queryId, params);
        setQueryDataByQueryId((prev) => ({ ...prev, [cacheKey]: chartQueryData }));
        // Sin queryData global el dashboard “solo gráficos” no se consideraba guardable
        setQueryData((prev) => prev || chartQueryData);
      }
      if (chartConfig.queryId != null && queryMeta?.queryText) {
        setQueryMetaByQueryId((prev) => ({
          ...prev,
          [chartConfig.queryId]: {
            queryText: queryMeta.queryText,
            database: queryMeta.database,
            connectionId: queryMeta.connectionId,
          },
        }));
        setQuery((prev) => prev || queryMeta.queryText);
      }
      setQueryEditorOpen(false);
      setQueryEditorInitialChart(null);
      setQueryEditorInitialQueryData(null);
      setQueryEditorInitialQueryId(null);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHighlightChartId(newChart.id));
      });
      return { newChart, nextCharts, nextLayout, nextLibrary, chartQueryData };
    },
    [charts, chartLayout, chartLibrary, globalFilterValues, globalFilterParamAliases, globalFilterParamNames, resolveDashboardQueryDataKey]
  );

  const handleUpdateChartFromEditor = useCallback((chartId, chartConfig) => {
    setCharts((prevCharts) =>
      prevCharts.map((c) =>
        c.id === chartId ? enrichChartConfig({ ...c, ...chartConfig, id: c.id }) : c
      )
    );
  }, []);

  const handleHideChartFromGrid = useCallback((chartId) => {
    setChartLayout((prev) => prev.filter((item) => item.i !== chartId));
    setChartLibrary((prev) => markHiddenInLibrary(prev, chartId));
  }, []);

  const handleRemoveChartFromDashboard = useCallback((chartId) => {
    if (!chartId) return;
    setCharts((prev) => prev.filter((c) => c.id !== chartId));
    setChartLayout((prev) => prev.filter((l) => l.i !== chartId));
    setChartLibrary((prev) => removeFromLibrary(prev, chartId));
    showSuccess('Gráfico eliminado del dashboard');
  }, [showSuccess]);

  const handleShowChartOnGrid = useCallback((chartId) => {
    if (!chartId) return;
    setChartLibrary((prev) => showChartOnGrid(prev, chartId));
    setChartLayout((prev) => {
      if (prev.some((l) => l.i === chartId)) return prev;
      const maxY = prev.length > 0 ? Math.max(...prev.map((item) => item.y + item.h)) : 0;
      const chart = charts.find((c) => c.id === chartId);
      return [...prev, defaultGridLayoutItem(chartId, chart?.type, { y: maxY })];
    });
    setHighlightChartId(chartId);
  }, [charts]);

  const reloadSavedDashboards = useCallback(async () => {
    try {
      const res = await dashboardsGetMutation.mutateAsync(getAuthHeaders());
      if (res.data.success) {
        setSavedDashboards(res.data.dashboards || []);
        return res.data.dashboards || [];
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  }, [getAuthHeaders]);

  const syncOpenDashboardFromServer = useCallback(
    async (dashboardId = currentDashboardId) => {
      if (dashboardId == null) return;
      const list = await reloadSavedDashboards();
      if (!list) return;
      const dash = list.find(
        (d) => d && (d.id === dashboardId || String(d.id) === String(dashboardId))
      );
      if (!dash) return;
      setCharts((dash.charts || []).map((c) => enrichChartConfig(c)));
      setChartLibrary(normalizeDashboardLibrary(dash));
      const visible = visibleChartsFromDashboard(dash);
      const visibleIds = new Set(visible.map((c) => c.id));
      setChartLayout((dash.layout || []).filter((l) => visibleIds.has(l.i)));
    },
    [currentDashboardId, reloadSavedDashboards]
  );

  const addOriginalToDashboardMutation = useMutation({
    mutationFn: ({ originalId, body, headers }) =>
      axios.post(`/api/chart-originals/${encodeURIComponent(originalId)}/add-to-dashboard`, body, { headers }),
  });

  const handleAddOriginalToDashboard = useCallback(
    async (original) => {
      if (!original?.id) return null;
      if (!currentDashboardId) {
        showWarning('Guarda el dashboard antes de agregar gráficos de la colección');
        return null;
      }
      const res = await addOriginalToDashboardMutation.mutateAsync({
        originalId: original.id,
        body: { dashboardId: currentDashboardId },
        headers: getAuthHeaders(),
      });
      if (!res.data.success) {
        showError(res.data.error || 'No se pudo agregar el gráfico');
        return null;
      }
      const { chart, layoutItem, isReference } = res.data;
      const nextCharts = [...charts, chart];
      const nextLayout = [...chartLayout, layoutItem];
      // isReference lo decide el backend: false si el original todavia no se
      // usaba en ningun dashboard (recien duplicado), true si ya estaba en
      // uso en otro lado. Ver server/chartOriginals.js: addOriginalToDashboard.
      const nextLibrary = upsertLibraryEntry(chartLibrary, chart, {
        visibleOnGrid: true,
        isReference: isReference === true,
      });
      setCharts(nextCharts);
      setChartLayout(nextLayout);
      setChartLibrary(nextLibrary);
      setHighlightChartId(chart.id);
      await reloadSavedDashboards();
      await loadChartOriginals();
      showSuccess(`«${original.title || chart.label}» agregado al dashboard`);
      return chart;
    },
    [
      charts,
      chartLayout,
      chartLibrary,
      currentDashboardId,
      getAuthHeaders,
      loadChartOriginals,
      reloadSavedDashboards,
      showError,
      showSuccess,
      showWarning,
    ]
  );

  const makeInstanceIndependentMutation = useMutation({
    mutationFn: ({ body, headers }) =>
      axios.post('/api/chart-originals/instances/make-independent', body, { headers }),
  });

  const handleMakeInstanceIndependent = useCallback(
    async ({ dashboardId, instanceId }) => {
      if (!dashboardId || !instanceId) return null;
      try {
        const res = await makeInstanceIndependentMutation.mutateAsync({
          body: { dashboardId, instanceId },
          headers: getAuthHeaders(),
        });
        if (!res.data.success) {
          showError(res.data.error || 'No se pudo convertir el gráfico');
          return null;
        }
        const { chart } = res.data;
        if (String(dashboardId) === String(currentDashboardId)) {
          setCharts((prev) => prev.map((c) => (c.id === instanceId ? chart : c)));
          setChartLibrary((prev) =>
            upsertLibraryEntry(prev, chart, {
              isReference: false,
              visibleOnGrid:
                prev.find((e) => e.id === instanceId)?.visibleOnGrid !== false,
            })
          );
        }
        await reloadSavedDashboards();
        await loadChartOriginals();
        showSuccess('Gráfico convertido en original independiente');
        return { chart, original: res.data.original };
      } catch (err) {
        showError(err.response?.data?.error || err.message || 'No se pudo convertir el gráfico');
        return null;
      }
    },
    [
      currentDashboardId,
      getAuthHeaders,
      loadChartOriginals,
      reloadSavedDashboards,
      showError,
      showSuccess,
    ]
  );

  const handleChartLayoutChange = useCallback(
    (visibleLayoutUpdate) => {
      setChartLayout(visibleLayoutUpdate);
    },
    []
  );

  const visibleCharts = useMemo(() => {
    if (!chartLibrary?.length) return charts;
    const hidden = new Set(
      chartLibrary.filter((e) => e.visibleOnGrid === false).map((e) => e.id)
    );
    return charts.filter((c) => !hidden.has(c.id));
  }, [charts, chartLibrary]);

  const visibleLayout = useMemo(() => {
    const ids = new Set(visibleCharts.map((c) => c.id));
    return chartLayout.filter((l) => ids.has(l.i));
  }, [visibleCharts, chartLayout]);

  const hiddenDashboardCharts = useMemo(() => {
    if (!chartLibrary?.length || !charts?.length) return [];
    const hiddenIds = new Set(
      chartLibrary.filter((e) => e.visibleOnGrid === false).map((e) => e.id)
    );
    return charts
      .filter((c) => hiddenIds.has(c.id))
      .map((chart) => ({
        chart,
        libEntry: chartLibrary.find((e) => e.id === chart.id),
      }));
  }, [charts, chartLibrary]);

  const openChartBuilder = useCallback(
    (chart = null) => {
      requestLeave(() => {
        if (currentDashboardReadOnly) {
          showWarning('Este dashboard es de solo lectura (acceso concedido)');
          return;
        }
        if (chart && !chart.visualizationSpec && !isQuerylessChartType(chart.type)) {
          if (chart.queryId != null) {
            if (!isAdminUser) {
              showWarning('El editor SQL solo está disponible para administradores.');
              return;
            }
            // Gráfico creado con SQL manual: editar en el Editor SQL (QueryEditor), no en el
            // editor de gráficos guiado, que solo entiende visualizationSpec.
            setQueryEditorInitialQueryId(chart.queryId);
            setQueryEditorInitialChart(chart);
            setQueryEditorInitialQueryData(null);
            setQueryEditorOpen(true);
            return;
          }
          showWarning('Este gráfico se creó con SQL manual y no se puede editar en el editor de gráficos.');
          return;
        }
        const libEntry = chart?.id ? chartLibrary.find((e) => e.id === chart.id) : null;
        if (chart && isLibraryReference(libEntry)) {
          setBuilderGridReference({ chart, entry: libEntry });
          setEditingChartId(null);
          setChartBuilderOpen(true);
          return;
        }
        setBuilderGridReference(null);
        setEditingChartId(chart?.id || null);
        setChartBuilderOpen(true);
      });
    },
    [chartLibrary, showWarning, currentDashboardReadOnly, isAdminUser, requestLeave]
  );

  useEffect(() => {
    if (isAdminUser) return;
    setQueryEditorOpen(false);
    setAdminPanelOpen(false);
  }, [isAdminUser]);

  const handleDynamicFilterChange = useCallback((chartId, paramName, selectedValue) => {
    setCharts(prevCharts => prevCharts.map(c => 
      c.id === chartId && c.advancedConfig
        ? {
            ...c,
            advancedConfig: {
              ...c.advancedConfig,
              ...(c.advancedConfig.dynamicFilter && String(c.advancedConfig.dynamicFilter?.paramName || '').trim() === String(paramName || '').trim()
                ? { dynamicFilter: { ...c.advancedConfig.dynamicFilter, selectedValue } }
                : {}),
              ...(Array.isArray(c.advancedConfig.dynamicFilters)
                ? {
                    dynamicFilters: c.advancedConfig.dynamicFilters.map((f) =>
                      String(f?.paramName || '').trim() === String(paramName || '').trim()
                        ? { ...f, selectedValue }
                        : f
                    ),
                  }
                : {}),
            },
          }
        : c
    ));
  }, []);

  const saveDashboardMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/dashboards/save', body, { headers }),
  });

  const persistDashboard = useCallback(
    async ({
      charts: chartsOverride,
      layout: layoutOverride,
      queryData: queryDataOverride,
      query: queryOverride,
      chartLibrary: chartLibraryOverride,
      silent = false,
    } = {}) => {
      if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
        if (!silent) showWarning('Debes estar autenticado para guardar dashboards');
        return null;
      }
      if (currentDashboardReadOnly) {
        if (!silent) showWarning('Este dashboard es de solo lectura (acceso concedido)');
        return null;
      }

      const chartsToSave = chartsOverride != null ? chartsOverride : charts;
      const layoutToSave = layoutOverride != null ? layoutOverride : chartLayout;
      const libraryToSave = chartLibraryOverride != null ? chartLibraryOverride : chartLibrary;
      const queryDataToSave = queryDataOverride !== undefined ? queryDataOverride : queryData;
      const queryToSave = queryOverride !== undefined ? queryOverride : query;

      if ((!chartsToSave || chartsToSave.length === 0) && !queryDataToSave) {
        if (!silent) showWarning('No hay gráficos ni datos para guardar');
        return null;
      }

      let name = currentDashboardName;
      if (!name || !name.trim()) {
        name = `Dashboard ${new Date().toLocaleString('es-ES')}`;
        setCurrentDashboardName(name);
      }

      const dashboard = sanitizeDashboardForSave({
        // Solo enviar id si ya existe en BD; los nuevos usan IDENTITY del servidor
        ...(currentDashboardId != null ? { id: currentDashboardId } : {}),
        name,
        query: queryToSave || '',
        queryData: queryDataToSave || null,
        charts: chartsToSave || [],
        chartLibrary: libraryToSave || [],
        layout: layoutToSave || [],
        database: selectedDatabase,
        timezone: dashboardTimezone,
        globalFilterParamNames,
        globalFilterValues,
        presentationMode,
        createdAt: new Date().toISOString(),
      });

      try {
        const response = await saveDashboardMutation.mutateAsync({ body: dashboard, headers: getAuthHeaders() });

        if (response.data.success) {
          dashboard.id = response.data.id;
          if (response.data.publicId) {
            dashboard.publicId = response.data.publicId;
          }
          setCurrentDashboardId(dashboard.id);
          const existingIndex = savedDashboards.findIndex(
            (d) => d && (d.id === dashboard.id || String(d.id) === String(dashboard.id))
          );
          let updatedDashboards;
          if (existingIndex >= 0) {
            updatedDashboards = [...savedDashboards];
            updatedDashboards[existingIndex] = dashboard;
            if (!silent) showSuccess(`Dashboard "${name}" actualizado`);
          } else {
            updatedDashboards = [...savedDashboards, dashboard];
            if (!silent) showSuccess(`Dashboard "${name}" guardado`);
          }
          setSavedDashboards(updatedDashboards);
          setCollectionRefreshKey((k) => k + 1);
          syncDashboardIdInUrl(dashboard);
          rememberLastDashboard(dashboard, userId, managerId || adminId);
          return dashboard;
        }
        if (!silent) showError(response.data.error || 'No se pudo guardar el dashboard');
        return null;
      } catch (error) {
        console.error('Error al guardar dashboard en BD:', error);
        if (!silent) {
          showError(
            error.response?.data?.error || error.message || 'Error al guardar dashboard. Intenta de nuevo.'
          );
        }
        return null;
      }
    },
    [
      isAuthenticated,
      authToken,
      managerId,
      adminId,
      userId,
      charts,
      chartLibrary,
      chartLayout,
      queryData,
      query,
      currentDashboardName,
      currentDashboardId,
      currentDashboardReadOnly,
      selectedDatabase,
      dashboardTimezone,
      globalFilterParamNames,
      globalFilterValues,
      presentationMode,
      savedDashboards,
      getAuthHeaders,
      showWarning,
      showSuccess,
      showError,
    ]
  );

  const handleSaveDashboard = async () => {
    requestLeave(async () => {
      dashboardNameEditorRef.current?.commitPendingEdit();
      await persistDashboard();
    });
  };

  // Función para guardar el dashboard actual automáticamente con nombre temporal
  const handleSaveCurrentDashboardTemporarily = async () => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      throw new Error('No autenticado');
    }
    if (!queryData && !(charts && charts.length)) {
      return null;
    }
    return persistDashboard({ silent: true });
  };

  // Obtener el dashboard actual como objeto (incl. id real si está cargado, para que la previsualización del reporte use los mismos filtros)
  const getCurrentDashboard = () => {
    if (!queryData && !(charts && charts.length)) return null;

    return dashboardForDisplay({
      id: currentDashboardId != null ? currentDashboardId : undefined,
      name: currentDashboardName || '',
      query: query,
      queryData: queryData,
      charts: charts,
      layout: chartLayout,
      chartLibrary: chartLibrary,
      database: selectedDatabase,
      timezone: dashboardTimezone,
      globalFilterParamNames: globalFilterParamNames,
      globalFilterValues: globalFilterValues,
      globalFilterParamAliases: globalFilterParamAliases,
      presentationMode: presentationMode,
    });
  };

  // Verificar si el dashboard actual está guardado
  const isDashboardSaved = () => {
    const hasContent = Boolean(queryData) || (charts && charts.length > 0);
    if (!currentDashboardName || !hasContent) return false;

    const currentQuery = (query || '').trim();
    const currentChartsStr = JSON.stringify(charts || []);
    const currentLayoutStr = JSON.stringify(chartLayout || []);
    const currentLibraryStr = JSON.stringify(chartLibrary || []);

    const currentGlobalParamStr = JSON.stringify(globalFilterParamNames || []);
    const currentGlobalValuesStr = JSON.stringify(globalFilterValues || {});
    const contentMatches = (d) => {
      if (!d) return false;
      if (d.name !== currentDashboardName) return false;
      if ((d.query || '').trim() !== currentQuery) return false;
      if (d.database !== selectedDatabase) return false;
      if (JSON.stringify(d.charts || []) !== currentChartsStr) return false;
      if (JSON.stringify(d.layout || []) !== currentLayoutStr) return false;
      if (JSON.stringify(normalizeDashboardLibrary(d)) !== currentLibraryStr) return false;
      if (JSON.stringify(d.globalFilterParamNames || []) !== currentGlobalParamStr) return false;
      if (JSON.stringify(d.globalFilterValues || {}) !== currentGlobalValuesStr) return false;
      return true;
    };

    if (currentDashboardId != null) {
      const loaded = savedDashboards.find(
        (d) => d && (d.id === currentDashboardId || String(d.id) === String(currentDashboardId))
      );
      if (loaded && contentMatches(loaded)) return true;
    }
    return savedDashboards.some(contentMatches);
  };

  const handleLoadDashboard = (dashboard) => {
    setQuery(dashboard.query || '');
    setQueryData(dashboard.queryData || null);
    const lib = normalizeDashboardLibrary(dashboard);
    setChartLibrary(lib);
    const enriched = (dashboard.charts || []).map((chart) => enrichChartConfig(chart));
    setCharts(enriched);
    const visible = visibleChartsFromDashboard({ ...dashboard, charts: enriched });
    const visibleIds = new Set(visible.map((c) => c.id));
    setChartLayout((dashboard.layout || []).filter((l) => visibleIds.has(l.i)));
    setCurrentDashboardName(dashboard.name || '');
    setCurrentDashboardId(dashboard.id || null);
    currentDashboardIdRef.current = dashboard.id || null;
    setCurrentDashboardAccessLevel(resolveDashboardAccessLevel(dashboard));
    setQueryDataByQueryId({});
    dashboardNameEditorRef.current?.resetEditing();
    setGlobalFilterParamNames(Array.isArray(dashboard.globalFilterParamNames) ? dashboard.globalFilterParamNames : []);
    setGlobalFilterValues(dashboard.globalFilterValues && typeof dashboard.globalFilterValues === 'object' ? dashboard.globalFilterValues : {});
    setPresentationMode(Boolean(dashboard.presentationMode));
    if (dashboard.database) {
      setSelectedDatabase(dashboard.database);
    }
    setDashboardTimezone(resolveViewTimezone(dashboard));
    if (dashboard.id != null) {
      syncDashboardIdInUrl(dashboard);
      rememberLastDashboard(dashboard, userId, managerId || adminId);
    }
  };

  const dashboardByIdGetMutation = useMutation({
    mutationFn: ({ dashboardId, headers }) =>
      axios.get(`/api/dashboards/${encodeURIComponent(dashboardId)}`, { headers }),
  });

  useEffect(() => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) return;
    if (urlDashboardLoadedRef.current) return;
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('printReport') === '1') {
      return;
    }
    const ownerId = managerId || adminId;
    const urlId = pendingDashboardIdFromUrlRef.current;
    let targetId = urlId;
    let fromStorage = false;
    if (targetId == null && !isEmbedView()) {
      targetId = readLastDashboardId(userId, ownerId);
      fromStorage = targetId != null;
    }
    if (targetId == null) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardByIdGetMutation.mutateAsync({ dashboardId: targetId, headers: getAuthHeaders() });
        if (cancelled) return;
        if (!res.data?.success || !res.data.dashboard) {
          if (fromStorage) forgetLastDashboard(userId, ownerId);
          else showWarning('No se encontró el dashboard del enlace');
          urlDashboardLoadedRef.current = true;
          setUrlDashboardLoadDone(true);
          return;
        }
        const dashboard = res.data.dashboard;
        handleLoadDashboard(dashboard);
        setSavedDashboards((prev) => {
          const exists = prev.some(
            (d) => d && (d.id === dashboard.id || String(d.id) === String(dashboard.id))
          );
          return exists ? prev : [...prev, dashboard];
        });
        urlDashboardLoadedRef.current = true;
        setUrlDashboardLoadDone(true);
      } catch (err) {
        if (!cancelled) {
          if (fromStorage) forgetLastDashboard(userId, ownerId);
          else showWarning(err.response?.data?.error || 'No se pudo cargar el dashboard del enlace');
          urlDashboardLoadedRef.current = true;
          setUrlDashboardLoadDone(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authToken, managerId, adminId, userId, getAuthHeaders, showWarning]);

  const resolveDashboardForShare = useCallback(
    (dashboardOrId) => {
      if (dashboardOrId && typeof dashboardOrId === 'object') return dashboardOrId;
      const id = dashboardOrId ?? currentDashboardId;
      if (id == null) return null;
      return savedDashboards.find(
        (d) => d && (d.id === id || String(d.id) === String(id))
      );
    },
    [currentDashboardId, savedDashboards]
  );

  const handleCopyDashboardLink = useCallback(
    (dashboardOrId) => {
      const dashboard = resolveDashboardForShare(dashboardOrId);
      const code = getDashboardShareCode(dashboard);
      if (!code) {
        showWarning('Guarda el dashboard antes de copiar el ID');
        return;
      }
      copyTextToClipboard(code).then((copied) => {
        if (copied) showSuccess(`ID copiado: ${code}`);
        else window.prompt('Copia este ID:', code);
      });
    },
    [resolveDashboardForShare, showWarning, showSuccess]
  );

  const handleNewDashboard = () => {
    setQuery('');
    setQueryData(null);
    setCharts([]);
    setChartLibrary([]);
    setChartLayout([]);
    setCurrentDashboardName('');
    setCurrentDashboardId(null);
    setCurrentDashboardAccessLevel(null);
    setQueryDataByQueryId({});
    setGlobalFilterParamNames([]);
    setGlobalFilterValues({});
    setPresentationMode(false);
    dashboardNameEditorRef.current?.resetEditing();
    syncDashboardIdInUrl(null);
    forgetLastDashboard(userId, managerId || adminId);
  };

  const deleteDashboardMutation = useMutation({
    mutationFn: ({ dashboardId, headers }) => axios.delete(`/api/dashboards/${dashboardId}`, { headers }),
  });

  const handleDeleteDashboard = async (dashboardId, event) => {
    event.stopPropagation();
    if (!isAuthenticated || !authToken || (!managerId && !adminId)) {
      showWarning('Debes estar autenticado para eliminar dashboards');
      return;
    }

    if (window.confirm('¿Eliminar este dashboard?')) {
      try {
        await deleteDashboardMutation.mutateAsync({ dashboardId, headers: getAuthHeaders() });
        
        const removed = savedDashboards.find(
          (d) => d && (d.id === dashboardId || String(d.id) === String(dashboardId))
        );
        forgetLastDashboardIfMatch(userId, managerId || adminId, removed || dashboardId);
        const updatedDashboards = savedDashboards.filter(d => d.id !== dashboardId);
        setSavedDashboards(updatedDashboards);
        showSuccess('Dashboard eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar dashboard de BD:', error);
        showError('Error al eliminar dashboard. Por favor, intenta de nuevo.');
      }
    }
  };


  // Crear tema de Material-UI basado en la paleta VSaaS
  const theme = createTheme({
    palette: {
      primary: vsaasTheme.primary,
      secondary: vsaasTheme.secondary,
      background: vsaasTheme.background,
      text: vsaasTheme.text,
    },
    typography: vsaasTheme.typography,
  });

  // Vista de impresión de reporte (misma que previsualización) para captura PDF vía Puppeteer
  const printParams = typeof window !== 'undefined' && window.location && window.location.search
    ? (() => {
        const p = new URLSearchParams(window.location.search);
        const reportId = p.get('reportId');
        if (p.get('printReport') === '1' && reportId) return { reportId };
        return null;
      })()
    : null;
  if (printParams) {
    return (
      <ThemeProvider theme={theme}>
        <StyleTemplateProvider loadPalette={() => Promise.resolve(null)} savePalette={() => {}}>
          <AppThemeFromContext>
            <PrintReportPage reportId={printParams.reportId} />
          </AppThemeFromContext>
        </StyleTemplateProvider>
      </ThemeProvider>
    );
  }

  // Autenticación pendiente → spinner; fallida → error simple
  if (authStatus === 'pending') {
    return (
      <ThemeProvider theme={theme}>
        <AuthLoading />
      </ThemeProvider>
    );
  }

  if (authStatus !== 'authenticated') {
    return (
      <ThemeProvider theme={theme}>
        <AuthRequired message={authError || 'Error de autorización.'} />
      </ThemeProvider>
    );
  }

  const embedView = isEmbedView();
  const embedDashboardId = embedView ? parseDashboardIdFromUrl() : null;

  if (embedView) {
    const embedHasCharts = visibleCharts.length > 0;
    const embedLoading = embedDashboardId && !urlDashboardLoadDone && currentDashboardId == null;
    const embedNotFound = embedDashboardId && urlDashboardLoadDone && currentDashboardId == null;

    return (
      <ThemeProvider theme={theme}>
        <StyleTemplateProvider loadPalette={loadStylePalette} savePalette={saveStylePalette}>
          <AppThemeFromContext>
            <Box
              sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                p: { xs: 1, sm: 2 },
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {!embedDashboardId && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Falta el parámetro <code>dashboardId</code> en la URL.
                  </Typography>
                </Box>
              )}
              {embedLoading && <AuthLoading />}
              {embedNotFound && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No se encontró el dashboard <code>{embedDashboardId}</code>.
                  </Typography>
                </Box>
              )}
              {embedDashboardId && !embedNotFound && !embedLoading && !embedHasCharts && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography color="text.secondary">Este dashboard no tiene gráficos.</Typography>
                </Box>
              )}
              {embedDashboardId && embedHasCharts && (
                <Box sx={{ flex: 1, minHeight: 400, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <ChartGrid
                  key={currentDashboardId ?? embedDashboardId}
                  embedMode
                  dashboardTimezone={dashboardTimezone}
                  queryData={queryData}
                  queryDataByQueryId={queryDataByQueryId}
                  getQueryDataKey={resolveDashboardQueryDataKey}
                  getAuthHeaders={getAuthHeaders}
                  queryMetaByQueryId={queryMetaByQueryId}
                  runQueryWithParams={runQueryWithParams}
                  runOptionsQuery={runOptionsQuery}
                  runFilterOptionsQuery={runFilterOptionsQuery}
                  onDynamicFilterChange={() => {}}
                  globalFilterParamNames={globalFilterParamNames}
                  globalFilterValues={globalFilterValues}
                  onGlobalFilterValuesChange={setGlobalFilterValues}
                  globalFilterParamAliases={globalFilterParamAliases}
                  presentationMode={presentationMode}
                  chartsDataLoading={chartsDataLoading}
                  charts={visibleCharts}
                  onChartsChange={() => {}}
                  weatherDataByChartId={weatherDataByChartId}
                  onWeatherDataLoaded={handleWeatherDataLoaded}
                  query={query}
                  onQueryExecute={() => {}}
                  selectedDatabase={selectedDatabase}
                  selectedConnectionId={selectedConnectionId}
                  savedQueries={savedQueries}
                  selectedQueryId={selectedQueryId}
                  onQuerySelect={() => {}}
                  layout={visibleLayout}
                  onLayoutChange={() => {}}
                  dashboardName={currentDashboardName}
                  currentDashboardId={currentDashboardId}
                  sharedOriginalIds={sharedOriginalIds}
                  chartLibrary={chartLibrary}
                />
                </Box>
              )}
            </Box>
          </AppThemeFromContext>
        </StyleTemplateProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <StyleTemplateProvider loadPalette={loadStylePalette} savePalette={saveStylePalette}>
      <AppThemeFromContext>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: 0 }}>
        <AppBar position="static" color="primary" sx={{ bgcolor: 'primary.main' }}>
          <Toolbar sx={{ flexWrap: 'wrap', gap: 0.5, py: { xs: 0.5, sm: 1 } }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <DashboardNameEditor
                ref={dashboardNameEditorRef}
                name={currentDashboardName}
                accessLevel={currentDashboardAccessLevel}
                readOnly={currentDashboardReadOnly}
                showUnsavedChip={!currentDashboardReadOnly && !isDashboardSaved() && (queryData || charts.length > 0)}
                isCompactHeader={isCompactHeader}
                onSave={setCurrentDashboardName}
                onShowWarning={showWarning}
              />
              <IconButton
                color="inherit"
                onClick={() => handleCopyDashboardLink()}
                title="Copiar ID del dashboard"
                size="small"
                disabled={currentDashboardId == null}
              >
                <LinkIcon />
              </IconButton>
              {!currentDashboardReadOnly && (
                <IconButton
                  color="inherit"
                  onClick={handleSaveDashboard}
                  title="Guardar Dashboard"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <SaveIcon />
                </IconButton>
              )}
              <IconButton
                color="inherit"
                onClick={() => requestLeave(() => setDashboardListOpen(true))}
                title="Cargar Dashboard"
                size="small"
              >
                <FolderOpenIcon />
              </IconButton>
              {!currentDashboardReadOnly && (
                <IconButton
                  color="inherit"
                  onClick={() => openChartBuilder()}
                  title="Editor de gráficos"
                  size="small"
                  disabled={!selectedDatabase}
                >
                  <BarChartIcon />
                </IconButton>
              )}
            </Box>
          <IconButton
            color="inherit"
            onClick={() => setPresentationMode((prev) => !prev)}
            title={presentationMode ? 'Salir de modo presentación' : 'Modo presentación (oculta acciones y filtros técnicos)'}
            sx={presentationMode ? { backgroundColor: 'rgba(255, 255, 255, 0.15)' } : undefined}
          >
            {presentationMode ? <PresentToAllIcon /> : <PresentToAllOutlinedIcon />}
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => requestLeave(() => setReportManagerOpen(true))}
            title="Gestionar Reportes"
          >
            <DescriptionIcon />
          </IconButton>
          {(queryData || charts.length > 0) && (
            <IconButton
              color="inherit"
              disabled={dashboardExportPending}
              onClick={handleExportDashboardExcel}
              title={dashboardExportPending ? 'Esperando a que terminen de cargar los gráficos' : 'Descargar Excel del dashboard'}
            >
              <FileDownloadIcon />
            </IconButton>
          )}
          {isAdminUser && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.35)',
                  mx: 0.75,
                  alignSelf: 'stretch',
                  my: 0.75,
                }}
              />
              <IconButton
                color="inherit"
                onClick={() => requestLeave(() => setQueryEditorOpen(true))}
                title="Editor SQL (consultas manuales, exploración de datos)"
              >
                <CodeIcon />
              </IconButton>
              <IconButton
                color="inherit"
                onClick={() => requestLeave(() => setAdminPanelOpen(true))}
                title="Configuración (apariencia, conexiones, modelo, editor de gráficos)"
              >
                <SettingsIcon />
              </IconButton>
            </>
          )}
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: { xs: 140, md: 200 },
              ml: { xs: 0, sm: 2 },
              display: hideTimezoneInHeader ? 'none' : 'flex',
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-focused': {
                  color: 'white',
                },
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            }}
          >
            <InputLabel id="dashboard-timezone-label" htmlFor="dashboard-timezone-input">Zona Horaria</InputLabel>
            <Select
              labelId="dashboard-timezone-label"
              id="dashboard-timezone-select"
              inputProps={{ id: 'dashboard-timezone-input' }}
              value={resolveTimezoneSelection(dashboardTimezone)}
              label="Zona Horaria"
              onChange={(e) => {
                const next = resolveTimezoneSelection(e.target.value);
                setDashboardTimezone(next);
                savePreferredTimezone(next, currentDashboardId);
              }}
            >
              {PRIMARY_TIMEZONE_IDS.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {formatTimezoneLabel(tz)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>
      
      <Box sx={{ flex: 1, overflow: { xs: 'auto', md: 'hidden' }, position: 'relative', minHeight: 0 }}>
        {/* Solo mostrar el dashboard, sin editor SQL */}
        <Box sx={{ height: { xs: 'auto', md: '100%' }, minHeight: { xs: '100%', md: 0 }, padding: { xs: 1, sm: 2 }, backgroundColor: 'background.default', boxSizing: 'border-box' }}>
          <ChartGrid 
            key={currentDashboardId ?? 'unsaved'}
            dashboardTimezone={dashboardTimezone}
            queryData={queryData} 
            queryDataByQueryId={queryDataByQueryId}
            getQueryDataKey={resolveDashboardQueryDataKey}
            getAuthHeaders={getAuthHeaders}
            queryMetaByQueryId={queryMetaByQueryId}
            runQueryWithParams={runQueryWithParams}
            runOptionsQuery={runOptionsQuery}
            runFilterOptionsQuery={runFilterOptionsQuery}
            onDynamicFilterChange={handleDynamicFilterChange}
            globalFilterParamNames={globalFilterParamNames}
            globalFilterValues={globalFilterValues}
            onGlobalFilterParamNamesChange={setGlobalFilterParamNames}
            onGlobalFilterValuesChange={setGlobalFilterValues}
            globalFilterParamAliases={globalFilterParamAliases}
            presentationMode={presentationMode}
            chartsDataLoading={chartsDataLoading}
            charts={visibleCharts}
            onChartsChange={setCharts}
            weatherDataByChartId={weatherDataByChartId}
            onWeatherDataLoaded={handleWeatherDataLoaded}
            query={query}
            onQueryExecute={handleQueryExecute}
            selectedDatabase={selectedDatabase}
            selectedConnectionId={selectedConnectionId}
            savedQueries={savedQueries}
            selectedQueryId={selectedQueryId}
            onQuerySelect={setSelectedQueryId}
            layout={visibleLayout}
            onLayoutChange={currentDashboardReadOnly ? undefined : handleChartLayoutChange}
            dashboardName={currentDashboardName}
            highlightChartId={highlightChartId}
            onOpenChartBuilder={currentDashboardReadOnly ? undefined : () => openChartBuilder()}
            onEditChart={currentDashboardReadOnly ? undefined : (chart) => openChartBuilder(chart)}
            onHideChart={currentDashboardReadOnly ? undefined : handleHideChartFromGrid}
            onRemoveChart={currentDashboardReadOnly ? undefined : handleRemoveChartFromDashboard}
            onShowChartOnGrid={currentDashboardReadOnly ? undefined : handleShowChartOnGrid}
            onMakeInstanceIndependent={currentDashboardReadOnly ? undefined : handleMakeInstanceIndependent}
            readOnlyMode={currentDashboardReadOnly}
            currentDashboardId={currentDashboardId}
            hiddenCharts={hiddenDashboardCharts}
            sharedOriginalIds={sharedOriginalIds}
            chartLibrary={chartLibrary}
          />
        </Box>
      </Box>

      {/* Dialog para editor SQL */}
      <Dialog 
        open={queryEditorOpen && isAdminUser} 
        onClose={() => {
          setQueryEditorOpen(false);
          setQueryEditorInitialQueryId(null);
          setQueryEditorInitialChart(null);
          setQueryEditorInitialQueryData(null);
        }} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { height: '95vh', maxWidth: '95vw' }
        }}
      >
        <DialogContent dividers sx={{ p: 0 }}>
              <QueryEditor 
                initialQuery={query || ''}
                initialQueryId={queryEditorInitialQueryId}
                initialChart={queryEditorInitialChart}
                initialQueryDataForChart={queryEditorInitialQueryData}
                onQueryExecute={(data, queryText, database) => {
                  handleQueryExecute(data, queryText, database);
                  // No cerrar automáticamente, dejar que el usuario decida
                }}
                selectedDatabase={selectedDatabase}
                availableDatabases={availableDatabases}
                selectedConnectionId={selectedConnectionId}
                dataConnections={dataConnections}
                onConnectionChange={async (connId) => {
                  setSelectedConnectionId(connId);
                  setSelectedDatabase('');
                  await loadDatabases(connId);
                  loadDynamicFilterSources(connId, '');
                }}
                savedQueries={savedQueries}
                onQueriesChange={setSavedQueries}
                onDatabaseChange={setSelectedDatabase}
                onAddChart={handleAddChartFromEditor}
                onUpdateChart={handleUpdateChartFromEditor}
                onCloseAfterChartSave={(chartId, payload) => {
                  if (payload?.queryId != null && payload?.queryData != null) {
                    const chartForCache =
                      payload.chart ||
                      chartsRef.current.find((c) => c.id === chartId) ||
                      (payload.chartConfig ? { id: chartId, ...payload.chartConfig } : null);
                    const params = buildParamsForChart(chartForCache || {}, {
                      globalFilterValues,
                      globalFilterParamAliases,
                      globalFilterParamNames,
                      getDynamicFilterValue: (c, paramName) =>
                        getChartDynamicFilters(c).find(
                          (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
                        )?.selectedValue,
                    });
                    const cacheKey = resolveDashboardQueryDataKey(payload.queryId, params);
                    setQueryDataByQueryId((prev) => ({ ...prev, [cacheKey]: payload.queryData }));
                    setQueryData((prev) => prev || payload.queryData);
                  }
                  setHighlightChartId(chartId);
                  setQueryEditorOpen(false);
                  setQueryEditorInitialChart(null);
                  setQueryEditorInitialQueryData(null);
                  setQueryEditorInitialQueryId(null);
                }}
                onSaveQuery={saveQuery}
                isAuthenticated={isAuthenticated}
                authToken={authToken}
                managerId={managerId}
                adminId={adminId}
                userId={userId}
                timezone={dashboardTimezone}
                dynamicFilterOptionSources={dynamicFilterOptionSources}
              />
        </DialogContent>
      </Dialog>

      <ChartBuilderDialog
        open={chartBuilderOpen}
        onClose={() => {
          setChartBuilderOpen(false);
          setEditingChartId(null);
          setBuilderGridReference(null);
        }}
        connectionId={selectedConnectionId}
        database={selectedDatabase}
        timezone={dashboardTimezone}
        getAuthHeaders={getAuthHeaders}
        initialChart={editingChartId ? charts.find((c) => c.id === editingChartId) : null}
        currentDashboardId={currentDashboardId}
        currentDashboardName={currentDashboardName}
        collectionRefreshKey={collectionRefreshKey}
        gridReferenceRequest={builderGridReference}
        chartLibrary={chartLibrary}
        onRestoreToGrid={handleShowChartOnGrid}
        onAddOriginalToDashboard={handleAddOriginalToDashboard}
        onMakeInstanceIndependent={handleMakeInstanceIndependent}
        onPublish={async (payload) => {
          if (payload.editingChartId) {
            const existing = charts.find((c) => c.id === payload.editingChartId);
            const publishedChart = withPreservedManualPositions(payload.chartConfig, existing);
            const publishedAdvanced = publishedChart.advancedConfig || {};
            const updatedChart = enrichChartConfig({
              ...existing,
              ...publishedChart,
              id: payload.editingChartId,
              originalChartId:
                payload.originalChartId ||
                publishedChart?.originalChartId ||
                existing?.originalChartId,
              advancedConfig: {
                ...publishedAdvanced,
                tableColumnFilters: existing?.advancedConfig?.tableColumnFilters,
                title: publishedChart.label || existing?.advancedConfig?.title,
              },
            }, payload.queryData?.data);
            const publishedTitle =
              publishedChart?.label ||
              publishedChart?.advancedConfig?.title ||
              updatedChart.label;
            if (publishedTitle) {
              updatedChart.label = publishedTitle;
              updatedChart.advancedConfig = {
                ...(updatedChart.advancedConfig || {}),
                title: publishedTitle,
              };
            }
            const nextCharts = charts.map((c) => {
              if (c.id === payload.editingChartId) return updatedChart;
              if (
                payload.dashboardsUpdated > 0 &&
                payload.originalChartId &&
                c.originalChartId === payload.originalChartId
              ) {
                const mergedBase = withPreservedManualPositions(payload.chartConfig, c);
                return enrichChartConfig(
                  {
                    ...c,
                    ...mergedBase,
                    id: c.id,
                    label: publishedTitle || c.label,
                    advancedConfig: {
                      ...(mergedBase.advancedConfig || {}),
                      tableColumnFilters: c.advancedConfig?.tableColumnFilters,
                      title: publishedTitle || c.advancedConfig?.title,
                    },
                  },
                  payload.queryData?.data
                );
              }
              return c;
            });
            let nextLibrary = upsertLibraryEntry(chartLibrary, updatedChart, {
              visibleOnGrid:
                chartLibrary.find((e) => e.id === payload.editingChartId)?.visibleOnGrid !== false,
            });
            if (payload.dashboardsUpdated > 0 && payload.originalChartId && publishedTitle) {
              nextLibrary = nextLibrary.map((e) =>
                e.originalChartId === payload.originalChartId ? { ...e, title: publishedTitle } : e
              );
            }
            setCharts(nextCharts);
            setChartLibrary(nextLibrary);
            if (payload.chartConfig?.queryId != null && payload.queryData != null) {
              const params = buildParamsForChart(payload.chartConfig, {
                globalFilterValues,
                globalFilterParamAliases,
                globalFilterParamNames,
                getDynamicFilterValue: (c, paramName) =>
                  getChartDynamicFilters(c).find(
                    (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
                  )?.selectedValue,
              });
              setQueryDataByQueryId((prev) => ({
                ...prev,
                [resolveDashboardQueryDataKey(payload.chartConfig.queryId, params)]: payload.queryData,
              }));
            }
            if (payload.queryData) {
              setQueryData((prev) => prev || payload.queryData);
            }
            if (payload.sql) {
              setQuery((prev) => prev || payload.sql);
            }
            setChartBuilderOpen(false);
            setEditingChartId(null);
            setHighlightChartId(payload.editingChartId);
            await persistDashboard({
              charts: nextCharts,
              layout: chartLayout,
              chartLibrary: nextLibrary,
              queryData: queryData || payload.queryData || null,
              query: query || payload.sql || '',
            });
            if (payload.dashboardsUpdated > 0) {
              await syncOpenDashboardFromServer(currentDashboardId);
              await loadChartOriginals();
            }
            return;
          }

          const chartConfig = {
            ...payload.chartConfig,
            originalChartId: payload.originalChartId || payload.chartConfig?.originalChartId,
          };
          const added = handleAddChartFromEditor(chartConfig, payload.queryData, {
            queryText: payload.sql,
            database: payload.database,
            connectionId: payload.connectionId,
          });
          setChartBuilderOpen(false);
          setEditingChartId(null);
          const chartsToSave = added?.nextCharts;
          const layoutToSave = added?.nextLayout;
          const nextLibrary = added?.nextLibrary || chartLibrary;
          if (chartsToSave?.length) {
            await persistDashboard({
              charts: chartsToSave,
              layout: layoutToSave,
              chartLibrary: nextLibrary,
              queryData: queryData || payload.queryData || null,
              query: query || payload.sql || '',
              silent: false,
            });
          }
        }}
      />

      {/* Report Manager */}
      <ReportManager
        open={reportManagerOpen}
        onClose={() => setReportManagerOpen(false)}
        savedDashboards={savedDashboards}
        isAuthenticated={isAuthenticated}
        authToken={authToken}
        managerId={managerId}
        adminId={adminId}
        currentDashboard={getCurrentDashboard()}
        onSaveCurrentDashboard={handleSaveCurrentDashboardTemporarily}
        suggestedTimezone={dashboardTimezone}
      />

      {/* Dashboard List */}
      <DashboardList
        open={dashboardListOpen}
        onClose={() => setDashboardListOpen(false)}
        dashboards={savedDashboards}
        onLoadDashboard={handleLoadDashboard}
        onNewDashboard={handleNewDashboard}
        onDashboardDuplicated={(dashboard) => {
          setSavedDashboards((prev) => [...prev, dashboard]);
          setCollectionRefreshKey((k) => k + 1);
          handleLoadDashboard(dashboard);
        }}
        onDeleteDashboard={(dashboardId) => {
          const updatedDashboards = savedDashboards.filter(d => d.id !== dashboardId);
          setSavedDashboards(updatedDashboards);
        }}
        onCopyDashboardLink={handleCopyDashboardLink}
        isAuthenticated={isAuthenticated}
        authToken={authToken}
        managerId={managerId}
        adminId={adminId}
      />

      {/* Panel de administración */}
      {isAdminUser && (
        <AdminPanel
          open={adminPanelOpen}
          onClose={() => setAdminPanelOpen(false)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
      </Box>
      </AppThemeFromContext>
      </StyleTemplateProvider>
    </ThemeProvider>
  );
}

export default App;
